import ClickAwayListener from 'react-click-away-listener';
import {Button, Icon} from '@ezcater/tapas';
import {faSpinnerThird} from '@fortawesome/pro-regular-svg-icons';
import {faLocationCrosshairs} from '@fortawesome/pro-solid-svg-icons';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import Ribbon, {RibbonState} from '@/components/global/layout/EventBar/Ribbon';
import {type AddressSearchAutocompleteSuffixProps} from './types';
import useAddressGeolocation from './useAddressGeolocation';

const AddressSearchAutocompleteSuffix: React.FC<AddressSearchAutocompleteSuffixProps> = ({
  geocodedAddressResultHelpers,
  inputHelpers,
  icon,
}) => {
  const {t} = useTranslation('common');
  const {
    getGeolocation: handleGetGeolocation,
    hasErrors,
    isLoading,
    setHasErrors,
  } = useAddressGeolocation({geocodedAddressResultHelpers, inputHelpers});

  return (
    <div className="-mr-2 flex">
      <Button
        aria-label={t('components.AddressSearchAutocomplete.locator.aria.label')}
        disabled={isLoading}
        className="p-1"
        onClick={handleGetGeolocation}
        variant="secondary"
      >
        {isLoading ? (
          <div aria-hidden className="flex animate-spin">
            <Icon icon={faSpinnerThird} size="xsmall" />
            <span className="sr-only">
              {t('components.AddressSearchAutocomplete.locator.aria.loading')}
            </span>
          </div>
        ) : (
          <Icon
            className={twJoin('text-lg', hasErrors ? 'text-pomegranate-400' : 'text-blueberry-400')}
            icon={icon ?? faLocationCrosshairs}
          />
        )}
      </Button>

      {hasErrors && (
        <ClickAwayListener onClickAway={() => setHasErrors(false)}>
          <div className="absolute -right-[.3rem] top-8 z-1350">
            <Ribbon
              arrowPlacement="up"
              message={
                <>
                  {t('components.AddressSearchAutocomplete.locator.error.line1')}
                  <br />
                  {t('components.AddressSearchAutocomplete.locator.error.line2')}
                </>
              }
              state={RibbonState.Error}
            />
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default AddressSearchAutocompleteSuffix;
